import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { API, authHeaders, clearToken, getToken } from "../lib/api";
import { AdminLogin } from "../components/AdminLogin";
import { ProductForm, ProductRow } from "../components/AdminProducts";
import { LogOut } from "lucide-react";

export default function AdminPage() {
  const [authenticated, setAuthenticated] = useState(false);
  const [checking, setChecking] = useState(true);
  const [products, setProducts] = useState([]);
  const [editing, setEditing] = useState(null);
  const [loadError, setLoadError] = useState("");

  const loadProducts = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/products`);
      setProducts(data);
      setLoadError("");
    } catch {
      setLoadError("Non riesco a caricare i capi. Riprova tra poco.");
    }
  }, []);

  useEffect(() => {
    const check = async () => {
      if (!getToken()) {
        setChecking(false);
        return;
      }
      try {
        await axios.get(`${API}/auth/me`, { headers: authHeaders() });
        setAuthenticated(true);
      } catch {
        clearToken();
      } finally {
        setChecking(false);
      }
    };
    check();
  }, []);

  useEffect(() => {
    if (authenticated) loadProducts();
  }, [authenticated, loadProducts]);

  const handleDelete = async (id) => {
    await axios.delete(`${API}/admin/products/${id}`, { headers: authHeaders() });
    if (editing?.id === id) setEditing(null);
    loadProducts();
  };

  const handleLogout = () => {
    clearToken();
    setAuthenticated(false);
  };

  if (checking) {
    return (
      <div className="min-h-screen bg-bone flex items-center justify-center">
        <p className="text-sm text-ink/60">Caricamento...</p>
      </div>
    );
  }

  if (!authenticated) {
    return <AdminLogin onSuccess={() => setAuthenticated(true)} />;
  }

  return (
    <div data-testid="admin-panel" className="min-h-screen bg-bone text-ink">
      <header className="border-b border-ink/10 bg-bone/90 backdrop-blur-md sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
          <div>
            <p className="text-xs uppercase tracking-[0.16em] text-cognac font-semibold">Area Riservata</p>
            <h1 className="font-display text-xl font-semibold">Gestione Vetrina</h1>
          </div>
          <div className="flex items-center gap-4">
            <a href="/" data-testid="admin-view-site" className="text-xs font-medium text-ink/60 hover:text-cognac transition-colors">
              Vedi il sito
            </a>
            <button
              data-testid="admin-logout"
              onClick={handleLogout}
              className="inline-flex items-center gap-2 text-xs font-medium text-ink/60 hover:text-red-600 transition-colors"
            >
              <LogOut className="w-4 h-4" aria-hidden="true" />
              Esci
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-10 grid grid-cols-1 md:grid-cols-2 gap-8 items-start">
        <ProductForm
          key={editing?.id || "new"}
          editing={editing}
          onSaved={() => {
            setEditing(null);
            loadProducts();
          }}
          onCancel={() => setEditing(null)}
        />
        <div className="space-y-3">
          <p data-testid="admin-product-count" className="text-xs uppercase tracking-[0.14em] text-ink/50 font-medium">
            {products.length} capi in vetrina
          </p>
          {loadError && (
            <p data-testid="admin-load-error" className="text-xs text-red-700">{loadError}</p>
          )}
          {products.map((p) => (
            <ProductRow key={p.id} product={p} onEdit={setEditing} onDelete={handleDelete} />
          ))}
          {products.length === 0 && !loadError && (
            <p data-testid="admin-empty-state" className="text-sm text-ink/50 italic">
              Nessun capo in vetrina. Aggiungine uno dal modulo a fianco.
            </p>
          )}
        </div>
      </main>
    </div>
  );
}
